import { isDisallowedSkillFilePath } from "@promptlayer/ui/skills/skill-file-support";
import { fileTypeFromBuffer } from "file-type";

import { ALL_IMPORT_PREFIXES } from "../provider-config";

import { normalizeRelativePath } from "./path-utils";

const SKIP_FILE_NAMES = new Set([".ds_store", "thumbs.db"]);
const MAX_SKILL_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const FILE_TYPE_SAMPLE_BYTES = 4100;

export interface ImportedSkillFile {
  path: string;
  content: string;
}

export interface SkillFolderImportResult {
  files: ImportedSkillFile[];
  rawPaths: string[];
  skippedOversizedCount: number;
  skippedUnsupportedCount: number;
}

interface StagedFile {
  file: File;
  relativePath: string;
  rawPath: string;
}

const isSkippedFolder = (name: string) => name.toLowerCase() === "__macosx";

const findImportPrefixMatch = (path: string) => {
  for (const prefix of ALL_IMPORT_PREFIXES) {
    if (path.startsWith(prefix)) {
      return { prefix, startIndex: 0 };
    }

    const nestedPrefix = `/${prefix}`;
    const nestedIndex = path.indexOf(nestedPrefix);
    if (nestedIndex >= 0) {
      return { prefix, startIndex: nestedIndex + 1 };
    }
  }

  return null;
};

const stripProviderPrefix = (path: string): string => {
  const match = findImportPrefixMatch(path);
  if (match) {
    return path.slice(match.startIndex + match.prefix.length);
  }

  return path;
};

const stripDetectedRoot = (staged: StagedFile[]): StagedFile[] => {
  let detectedRoot = "";

  for (const { rawPath } of staged) {
    const match = findImportPrefixMatch(rawPath);
    if (match && match.startIndex > 0) {
      const candidate = rawPath.slice(0, match.startIndex);
      if (!detectedRoot || candidate.length < detectedRoot.length) {
        detectedRoot = candidate;
      }
    }
  }

  if (!detectedRoot) {
    return staged;
  }

  return staged.map(entry => {
    if (entry.relativePath === entry.rawPath && entry.rawPath.startsWith(detectedRoot)) {
      const stripped = entry.rawPath.slice(detectedRoot.length);
      const normalized = normalizeRelativePath(stripped);
      if (normalized) {
        return { ...entry, relativePath: normalized };
      }
    }
    return entry;
  });
};

interface ResolvedPath {
  path: string;
  rawPath: string;
}

const buildImportedRelativePath = (
  parts: string[],
  options?: { stripRootSegment?: boolean },
): ResolvedPath | null => {
  if (parts.length === 0) {
    return null;
  }

  const firstSegment = parts[0] ?? "";
  const shouldStripRoot =
    options?.stripRootSegment === true && parts.length >= 2 && !firstSegment.startsWith(".");
  const rawPath = shouldStripRoot ? parts.slice(1).join("/") : parts.join("/");
  const relativePath = normalizeRelativePath(stripProviderPrefix(rawPath));

  if (!relativePath || relativePath.endsWith("/")) {
    return null;
  }

  const segments = relativePath.split("/").filter(Boolean);
  const lowerSegments = segments.map(s => s.toLowerCase());

  if (lowerSegments.some(s => s === "__macosx")) {
    return null;
  }

  const leaf = lowerSegments[lowerSegments.length - 1];
  if (leaf && SKIP_FILE_NAMES.has(leaf)) {
    return null;
  }

  return { path: relativePath, rawPath };
};

const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = "";
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }

  return btoa(binary);
};

const isProbablyTextContent = (bytes: Uint8Array): boolean => {
  if (bytes.length === 0) {
    return true;
  }

  if (bytes.includes(0)) {
    return false;
  }

  try {
    new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    return true;
  } catch {
    return false;
  }
};

const readImportedFileContent = async (file: File): Promise<string> => {
  const buffer = new Uint8Array(await file.arrayBuffer());
  const sample = buffer.subarray(0, Math.min(FILE_TYPE_SAMPLE_BYTES, buffer.length));
  const detectedType = await fileTypeFromBuffer(sample);
  const detectedMimeType = detectedType?.mime ?? file.type;

  if (!detectedMimeType && isProbablyTextContent(buffer)) {
    return new TextDecoder("utf-8").decode(buffer);
  }

  if (
    detectedMimeType?.startsWith("image/") ||
    detectedMimeType?.startsWith("video/") ||
    detectedMimeType?.startsWith("audio/") ||
    detectedMimeType === "application/pdf" ||
    detectedType !== undefined ||
    !isProbablyTextContent(buffer)
  ) {
    return `data:${detectedMimeType || "application/octet-stream"};base64,${bytesToBase64(buffer)}`;
  }

  return new TextDecoder("utf-8").decode(buffer);
};

const finalizeImportedFiles = async (staged: StagedFile[]): Promise<SkillFolderImportResult> => {
  const resolved = stripDetectedRoot(staged);

  const byPath = new Map<string, { file: File; rawPath: string }>();
  for (const { file, relativePath, rawPath } of resolved) {
    byPath.set(relativePath, { file, rawPath });
  }

  const rawPaths: string[] = [];
  let skippedOversizedCount = 0;
  let skippedUnsupportedCount = 0;
  const entries = await Promise.all(
    Array.from(byPath.entries()).map(async ([path, { file, rawPath }]) => {
      rawPaths.push(rawPath);
      if (file.size > MAX_SKILL_FILE_SIZE_BYTES) {
        skippedOversizedCount += 1;
        return null;
      }
      if (isDisallowedSkillFilePath(path)) {
        skippedUnsupportedCount += 1;
        return null;
      }
      try {
        return { path, content: await readImportedFileContent(file) };
      } catch {
        return null;
      }
    }),
  );

  return {
    files: entries.filter((entry): entry is ImportedSkillFile => entry !== null),
    rawPaths,
    skippedOversizedCount,
    skippedUnsupportedCount,
  };
};

const walkDirectoryHandle = async (
  handle: FileSystemDirectoryHandle,
  prefixParts: string[],
): Promise<StagedFile[]> => {
  const staged: StagedFile[] = [];
  const dirHandle = handle as FileSystemDirectoryHandle & {
    values(): AsyncIterable<FileSystemHandle>;
  };

  for await (const entry of dirHandle.values()) {
    if (entry.kind === "directory") {
      if (isSkippedFolder(entry.name)) {
        continue;
      }
      staged.push(
        ...(await walkDirectoryHandle(entry as FileSystemDirectoryHandle, [
          ...prefixParts,
          entry.name,
        ])),
      );
      continue;
    }

    if (entry.kind !== "file") {
      continue;
    }

    const resolved = buildImportedRelativePath([...prefixParts, entry.name]);
    if (!resolved) {
      continue;
    }

    staged.push({
      file: await (entry as FileSystemFileHandle & { getFile(): Promise<File> }).getFile(),
      relativePath: resolved.path,
      rawPath: resolved.rawPath,
    });
  }

  return staged;
};

interface WebkitEntry {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath: string;
  file?: (success: (file: File) => void, error?: (err: DOMException) => void) => void;
  createReader?: () => {
    readEntries: (
      success: (entries: WebkitEntry[]) => void,
      error?: (err: DOMException) => void,
    ) => void;
  };
}

const readWebkitDirectoryEntries = (entry: WebkitEntry): Promise<WebkitEntry[]> => {
  const reader = entry.createReader!();
  const allEntries: WebkitEntry[] = [];

  return new Promise((resolve, reject) => {
    const readNextBatch = () => {
      reader.readEntries(entries => {
        if (entries.length === 0) {
          resolve(allEntries);
          return;
        }
        allEntries.push(...entries);
        readNextBatch();
      }, reject);
    };
    readNextBatch();
  });
};

const walkWebkitEntry = async (entry: WebkitEntry): Promise<StagedFile[]> => {
  if (entry.isDirectory) {
    if (isSkippedFolder(entry.name)) {
      return [];
    }

    if (!entry.createReader) {
      return [];
    }

    const children = await readWebkitDirectoryEntries(entry);
    const results = await Promise.all(
      children.map(async child => {
        try {
          return await walkWebkitEntry(child);
        } catch {
          return [];
        }
      }),
    );
    return results.flat();
  }

  if (!entry.isFile || !entry.file) {
    return [];
  }

  const resolved = buildImportedRelativePath(entry.fullPath.split("/").filter(Boolean));
  if (!resolved) {
    return [];
  }

  const file = await new Promise<File>((resolve, reject) => {
    entry.file!(resolve, reject);
  });

  return [{ file, relativePath: resolved.path, rawPath: resolved.rawPath }];
};

export const canPickSkillFolderImport = () =>
  typeof window !== "undefined" &&
  typeof (window as unknown as Record<string, unknown>).showDirectoryPicker === "function";

export const pickSkillFolderImport = async (): Promise<SkillFolderImportResult> => {
  if (!canPickSkillFolderImport()) {
    return { files: [], rawPaths: [], skippedOversizedCount: 0, skippedUnsupportedCount: 0 };
  }

  const { showDirectoryPicker } = window as unknown as {
    showDirectoryPicker: () => Promise<FileSystemDirectoryHandle>;
  };
  const rootHandle = await showDirectoryPicker();
  const rootPrefixParts = rootHandle.name.startsWith(".") ? [rootHandle.name] : [];
  const staged = await walkDirectoryHandle(rootHandle, rootPrefixParts);

  return finalizeImportedFiles(staged);
};

const getRawImportPath = (file: File, index: number, totalFiles: number): string => {
  if (file.webkitRelativePath?.trim()) {
    return file.webkitRelativePath.trim().replace(/\\/g, "/");
  }

  if (totalFiles > 1) {
    return `__flat/${String(index).padStart(5, "0")}/${file.name || "file"}`;
  }

  return (file.name || "").replace(/\\/g, "/");
};

export const readSkillFolderImport = async (files: FileList): Promise<SkillFolderImportResult> => {
  const list = Array.from(files);
  const staged: StagedFile[] = [];

  for (const [index, file] of list.entries()) {
    const hasRel = Boolean(file.webkitRelativePath?.trim());
    const raw = getRawImportPath(file, index, list.length);

    const resolved = buildImportedRelativePath(raw.split("/").filter(Boolean), {
      stripRootSegment: hasRel,
    });
    if (resolved) {
      staged.push({ file, relativePath: resolved.path, rawPath: resolved.rawPath });
    }
  }

  return finalizeImportedFiles(staged);
};

export const readSkillDropImport = async (
  items: DataTransferItemList,
  files?: FileList,
): Promise<SkillFolderImportResult> => {
  const entries = Array.from(items)
    .map(
      item =>
        (
          item as DataTransferItem & { webkitGetAsEntry?: () => FileSystemEntry | null }
        ).webkitGetAsEntry?.() ?? null,
    )
    .filter((entry): entry is FileSystemEntry => entry !== null);

  if (entries.length === 0) {
    return files
      ? readSkillFolderImport(files)
      : { files: [], rawPaths: [], skippedOversizedCount: 0, skippedUnsupportedCount: 0 };
  }

  const results = await Promise.all(
    entries.map(async entry => {
      try {
        return await walkWebkitEntry(entry as unknown as WebkitEntry);
      } catch {
        return [];
      }
    }),
  );
  const staged = results.flat();
  return finalizeImportedFiles(staged);
};
